// FEAT-12 (spec §3.5 / §3.6) — "Run tests" for a language that runs on our
// servers. The browser has no C / C++ / Go engine, so the three Run buttons
// (the candidate's question, the recruiter's preview, the code editor) return
// early to here: one POST per click, through our own API (never cross-origin).
//
// Every run takes an AbortSignal: Next, Submit, time-up and unmount abort the
// request and a late answer is discarded — nothing ever waits for a run.
import {
  runCandidateCode,
  runRecruiterCode,
  type ServerRunHttp,
} from "@/lib/api";

export interface ServerRunRow {
  name: string;
  passed: boolean;
  output?: string;
  error?: string;
}

/** Why a run produced no results — each reads differently to a person. */
export type RunFailure =
  /** 503: the runner is busy, asleep or unreachable. */
  | { kind: "busy" }
  /** 429 from the per-minute throttle; `retryAfterS` drives the countdown. */
  | { kind: "throttled"; retryAfterS: number }
  /** 429 `run_cap_reached`: this attempt's runs are spent (submit still grades). */
  | { kind: "capped" }
  /** 409 `run_in_progress`: another tab's run is still going. */
  | { kind: "in_flight" }
  /** fetch threw: offline, DNS, connection reset. */
  | { kind: "network" }
  /** Recruiter only — 403 the Pro door, 409 LANGUAGE_NOT_AVAILABLE. */
  | { kind: "locked" }
  | { kind: "unavailable" }
  /** Anything else (a 410 after submit, a 422, a 5xx from our API). */
  | { kind: "error"; message: string };

/** FEAT-29: one of the person's OWN test cases, as the server answered it —
 *  in the order the cases were sent. `passed` null = it ran, nothing expected
 *  — or, with a `note`, it was not run (no room left in the example tests'
 *  job). */
export interface ServerOwnRow {
  passed: boolean | null;
  output?: string;
  error?: string;
  note?: string;
}

export interface ServerRunOutcome {
  results: ServerRunRow[];
  /** FEAT-29: the own cases' rows — never part of `results`. */
  ownRows: ServerOwnRow[];
  /** The compiler's stderr when the code did not compile (then no results). */
  compileError: string | null;
  failure: RunFailure | null;
}

const THROTTLE_FALLBACK_S = 10;

function detailCode(detail: unknown): string | undefined {
  if (detail && typeof detail === "object" && "code" in detail) {
    const code = (detail as { code?: unknown }).code;
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
}

function detailMessage(detail: unknown, status: number): string {
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "message" in detail) {
    const m = (detail as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return `The run failed (${status}).`;
}

export function classifyRefusal(
  http: Extract<ServerRunHttp<unknown>, { ok: false }>,
): RunFailure {
  const code = detailCode(http.detail);
  if (http.status === 503) return { kind: "busy" };
  if (http.status === 429) {
    if (code === "run_cap_reached") return { kind: "capped" };
    return { kind: "throttled", retryAfterS: http.retryAfterS ?? THROTTLE_FALLBACK_S };
  }
  if (http.status === 409 && code === "run_in_progress") return { kind: "in_flight" };
  if (http.status === 409 && code === "LANGUAGE_NOT_AVAILABLE") return { kind: "unavailable" };
  // Only a PLAN door is "locked" — 403 `<TIER>_REQUIRED` / `PLAN_REQUIRED`
  // (entitlements.py). The local ops.localhost CSRF block and a suspended
  // workspace's still-live cookie are 403s too and used to read "needs the Pro
  // plan" in the editor and the Preview (Build 1b verify follow-up); they now
  // fall through to the server's own words.
  if (http.status === 403 && code !== undefined && code.endsWith("_REQUIRED")) {
    return { kind: "locked" };
  }
  return { kind: "error", message: detailMessage(http.detail, http.status) };
}

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function toOutcome(
  http: ServerRunHttp<{
    results?: Array<{ name: string; passed: boolean; output?: string | null; error?: string | null }>;
    own_results?: Array<{
      passed?: boolean | null;
      output?: string | null;
      error?: string | null;
      note?: string | null;
    }>;
    compile_error?: string | null;
  }>,
): ServerRunOutcome {
  if (!http.ok) {
    return { results: [], ownRows: [], compileError: null, failure: classifyRefusal(http) };
  }
  return {
    results: (http.data.results ?? []).map((r) => ({
      name: r.name,
      passed: r.passed,
      ...(r.output ? { output: r.output } : {}),
      ...(r.error ? { error: r.error } : {}),
    })),
    ownRows: (http.data.own_results ?? []).map((r) => ({
      passed: r.passed ?? null,
      ...(r.output ? { output: r.output } : {}),
      ...(r.error ? { error: r.error } : {}),
      ...(r.note ? { note: r.note } : {}),
    })),
    compileError: http.data.compile_error ?? null,
    failure: null,
  };
}

/** The candidate's Run. `null` = aborted (the page moved on) — discard it.
 *  FEAT-29: `ownTests` ride in the same Run (see `runCandidateCode`). */
export async function runOnServerAsCandidate(
  args: {
    token: string;
    attemptId: string;
    position: number;
    code: string;
    ownTests?: Array<{ input: string; expected_output?: string }>;
  },
  signal: AbortSignal,
): Promise<ServerRunOutcome | null> {
  try {
    return toOutcome(
      await runCandidateCode(
        args.token,
        args.attemptId,
        args.position,
        args.code,
        signal,
        args.ownTests,
      ),
    );
  } catch (err) {
    if (isAbort(err) || signal.aborted) return null;
    return { results: [], ownRows: [], compileError: null, failure: { kind: "network" } };
  }
}

/** The recruiter's Run tests (editor, preview, back office). `null` = aborted. */
export async function runOnServerAsRecruiter(
  args: {
    language: string;
    code: string;
    tests: Array<{ name: string; input: string; expected_output: string }>;
    /** FEAT-12 phase 2: a typed question's signature (Java / PHP). */
    function_signature?: string | null;
  },
  signal: AbortSignal,
): Promise<ServerRunOutcome | null> {
  try {
    return toOutcome(await runRecruiterCode(args, signal));
  } catch (err) {
    if (isAbort(err) || signal.aborted) return null;
    return { results: [], ownRows: [], compileError: null, failure: { kind: "network" } };
  }
}
